import React, { useEffect, useRef, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import ShipeeziNewGrid, {
  Category,
  Column,
  DateOption,
  DefaultDateOptionsColumn,
  GeneralActions,
} from "Components/AppGrid/shipeezi-new-grid";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { BookingTabs } from "../../../../components/Booking/util";
import BadgeShipmentTrackingStatus from "../../../../components/Generic/BadgeShipmentTrackingStatus";
import { renderMilestoneDateByTypeAndReference } from "../../../../components/Generic/MilestoneDates";
import EditFieldModal, {
  EditFieldModalHandles,
  FieldType,
  OnSaveEditFieldModalProps,
} from "../../../../components/Generic/Modal/EditFieldModal";
import { NotificationToast } from "../../../../components/Generic/Toast/NotificationToast";
import { MoreOptions } from "../../../../components/MoreOptionsComponent/MoreOptions";
import TitlePage from "../../../../components/TitlePage";
import { useAppState } from "../../../../context/StateContext";
import { MilestoneReference, MilestoneType } from "../../../../models/milestone";
import { Filters, Searches } from "../../../../models/shipeezi.table";
import { Container } from "../../../../models/shipment.container";
import { Shipment, ShipmentContainerInfo } from "../../../../models/shipments";
import { capitalizeFirstLetterAllWorlds } from "../../../../util/capitalizeFirstLetter";
import { ShipmentStatus } from "../../../../util/enums";
import { formatDateWithoutTimeZone } from "../../../../util/Format";
import { ContainerKeys } from "../../../../util/queries";
import RenderContainer from "../Shipments/components/TrackableContainer";
import getShipmentOptions from "../Shipments/getShipmentOptions";
import ShipmentSideView, { ShipmentViewHandles } from "../Shipments/View";
import useAction from "./hooks/useAction";
import { ContainerGrid } from "./styles";

const Containers = () => {
  const { isFMVersion } = useAppState();
  const { t: translate } = useTranslation();
  const editModalRef = useRef<EditFieldModalHandles>(null);
  const sideViewRef = useRef<ShipmentViewHandles>(null);
  const history = useHistory<{ shipmentId: number; bookingTab: BookingTabs }>();

  const [containers, setContainers] = useState<Container[]>([] as Container[]);
  const [total, setTotal] = useState<number>(100);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [searches, setSearches] = useState<Searches>([]);
  const [searchFilters, setSearchFilters] = useState<Filters>();
  const [dateFilters, setDateFilters] = useState<Filters>();
  const [defaultBookingTab, setDefaultBookingTab] = useState<BookingTabs>(BookingTabs.TRACKING);

  const { getAllContainers, useUpdateContainer } = useAction();

  const queryClient = useQueryClient();
  const { statusOptions, containerTypeOptions } = getShipmentOptions();

  const handleFilter = (value: Filters) => {
    setSearchFilters(value);
    setPage(0);
  };

  const {
    data,
    isLoading: loadingContainers,
    isRefetching: refetchingContainers,
  } = getAllContainers(page, pageSize, searches, { ...searchFilters, ...dateFilters });
  const { mutate: updateContainer, isLoading: isUpdating } = useUpdateContainer();

  useEffect(() => {
    if (data) {
      setContainers(data.rows);
      setTotal(data.count);
    } else {
      setContainers([]);
      setTotal(0);
    }
  }, [data]);

  useEffect(() => {
    const { state } = history.location;

    if (state) {
      setDefaultBookingTab(state.bookingTab);
      handleOpenShipmentView(state.shipmentId);
    }
  }, [history.location]);

  const handleCurrentPage = (numPage: number) => {
    document.location.href = "#top";
    setPage(numPage - 1);
  };

  const handlePageSize = (pageSize: number) => {
    setPageSize(pageSize);
  };

  const handleSearch = (value: Searches) => {
    setSearches(value);
    setPage(0);
  };

  const handleOpenShipmentView = (shipmentId: number) => {
    sideViewRef.current?.open(Number(shipmentId));
  };

  const columns: Column[] = [
    {
      name: "container",
      title: `${translate("container")} #`,
      formatComponent: (value: string, row: Container) =>
        RenderContainer({
          container: value,
          marineTrafficId: String(row?.shipment?.shipmentTrackingId) ?? undefined,
          onTrackClick: () => handleOpenShipmentView(row?.shipment?.id ?? 0),
          isEmptyState:
            row?.shipment?.shipmentTracking?.shipmentStatus ===
              ShipmentStatus.TRACKING_NOT_AVAILABLE ||
            row?.shipment?.shipmentTracking?.shipmentStatus === ShipmentStatus.WAITING_FOR_UPDATE,
        }),
      searchIdentifier: "container",
      style: { whiteSpace: "nowrap", paddingRight: "10px" },
    },
    {
      name: "size",
      title: translate("type"),
      formatText: (value: string) =>
        containerTypeOptions?.find((option) => option?.value === value)?.label,
      hideSearch: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      name: "shipment",
      title: translate("consignee"),
      formatText: (shipment: Shipment) => shipment?.consignee?.name,
      searchIdentifier: "consignee",
      style: { whiteSpace: "nowrap" },
    },
    ...(isFMVersion
      ? [
          {
            name: "shipment",
            title: translate("project"),
            formatText: (shipment: Shipment) => shipment?.project?.name,
            searchIdentifier: "shipment.project.name",
            hideSearch: true,
            style: { whiteSpace: "nowrap" as any },
          },
        ]
      : []),
    {
      name: "shipment",
      title: translate("po"),
      searchIdentifier: "poNumbers",
      formatComponent: (shipment: Shipment) => {
        return (
          <MoreOptions
            options={shipment?.poNumbers ? shipment?.poNumbers?.split(" / ") : []}
            title={translate("orders")}
            manyOptionsToShow={1}
          />
        );
      },
      style: { whiteSpace: "nowrap", paddingRight: "10px" },
    },
    // {
    //   name: "cartonQty",
    //   title: translate("cartonQuantity"),
    //   hideSearch: true,
    //   style: { whiteSpace: "nowrap" },
    // },
    {
      name: "shipment",
      title: translate("portOfLoading"),
      formatText: (shipment: Shipment) => shipment?.portOfLoading?.siteCode,
      searchIdentifier: "portOfLoading",
      style: { whiteSpace: "nowrap" },
    },
    {
      name: "shipment",
      title: translate("portOfDischarge"),
      formatText: (shipment: Shipment) => shipment?.portOfDischarge?.siteCode,
      searchIdentifier: "portOfDischarge",
      style: { whiteSpace: "nowrap" },
    },
    {
      name: "shipment",
      title: translate("etaFinalPort"),
      formatComponent: (shipment: Shipment) =>
        renderMilestoneDateByTypeAndReference(
          shipment.milestones ?? [],
          MilestoneType.ESTIMATED,
          MilestoneReference.SHIPMENT.ESTIMATED.DESTINATION
        ),
      hideSearch: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      name: "shipment",
      title: translate("estimateClearedForPickUp"),
      formatText: (shipment: Shipment) => formatDateWithoutTimeZone(shipment?.scheduledDate),
      hideSearch: true,
      handleEdit: (rowId: number, row: ShipmentContainerInfo) =>
        editModalRef?.current?.open({
          id: rowId,
          value: row?.shipment?.scheduledDate,
          column: "shipment.scheduledDate",
          title: `${translate("container")} ${row.container}`,
          label: translate("estimateClearedForPickUp"),
          type: FieldType.DATE,
        }),
      style: { minWidth: "125px" },
      searchIdentifier: "shipment.scheduledDate",
    },
    {
      name: "shipment",
      title: translate("status"),
      formatComponent: (shipment: Shipment) =>
        shipment && (
          <BadgeShipmentTrackingStatus
            value={capitalizeFirstLetterAllWorlds(
              shipment?.shipmentTracking?.shipmentStatus || shipment?.statusShipment || ""
            )}
          />
        ),
      hideSearch: true,
      searchIdentifier: "statusShipment",
      style: { whiteSpace: "nowrap" },
      stickyRight: 0.1, // TODO: JOAO shipeezi grid needs to accept 0 as true value
    },
  ];

  const dateOptions: DateOption[] = [
    {
      column: "",
      name: translate("all"),
      type: DefaultDateOptionsColumn.HIDE_DATE_SELECTOR,
    },
    // {
    //   column: "etaFinalPort",
    //   name: translate("etaFinalPort"),
    // },
    {
      column: "scheduledDate",
      name: translate("estimateClearedForPickUp"),
    },
  ];

  const filterCategories: Category[] = [
    {
      name: translate("status"),
      column: "shipment.status_shipment",
      options: statusOptions,
    },
    {
      name: translate("containerType"),
      column: "size",
      options: containerTypeOptions,
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleSelectDate", "handleFilterBy"],
    },
    handleSearch: {
      inputPlaceholder: translate("typePressEnter"),
      inputSearchMode: "enter",
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: filterCategories,
      onChange: handleFilter,
    },
    handleSelectDate: {
      onChange: (dateFilters, dateType, period) => {
        setDateFilters?.({
          ...(dateOptions ? { dateType } : {}),
          period,
          ...dateFilters,
        } as unknown as Filters);
      },
      dateOptions,
    },
  };

  const onUpdate = async ({ id, column, value }: OnSaveEditFieldModalProps) => {
    const index = containers.findIndex((container) => container.id === id);
    const containerToUpdate = _.cloneDeep(containers[index]);

    _.set(containerToUpdate, column, value);

    await updateContainer(
      { id, data: containerToUpdate },
      {
        onSuccess: () => {
          setContainers((prev) => {
            const copy = [...prev];

            copy[index] = containerToUpdate;

            return copy;
          });
          editModalRef?.current?.close();
          queryClient.invalidateQueries({ queryKey: [ContainerKeys.getAll, "network"] });
          NotificationToast.success(translate("operationPerformedSuccessfully"));
        },
      }
    );
  };

  return (
    <div data-cy="containers-grid">
      <TitlePage title={translate("containers")} />
      <ContainerGrid>
        <ShipeeziNewGrid
          stickyActions
          namePage={translate("containers")}
          data={containers}
          totalItems={total}
          emptyData={translate("noInformationToBeShown")}
          loadingData={loadingContainers || refetchingContainers}
          columns={columns}
          currentPage={page}
          pageSize={pageSize}
          pageSizeList={[20, 40, 60, 100]}
          onChangePageSize={handlePageSize}
          onNextPage={handleCurrentPage}
          onPreviousPage={handleCurrentPage}
          onGoToPage={handleCurrentPage}
          generalActions={generalActions}
        />
      </ContainerGrid>
      <ShipmentSideView ref={sideViewRef} defaultTab={defaultBookingTab} />
      <EditFieldModal ref={editModalRef} onUpdate={onUpdate} loading={isUpdating} />
    </div>
  );
};

export default Containers;
